import type { DataTableInstance, DataTableRowData } from 'blocks/cx-data-table'
import { Button } from 'primitives/button'
import type { TableProps } from 'primitives/table'
import { Typography } from 'primitives/typography'

interface TableSelectionInfobarProps<TData extends DataTableRowData> extends TableProps {
  table: DataTableInstance<TData>
  itemLabel?: string
  isSelectDropdown?: boolean
}

export const TableSelectionInfobar = <TData extends DataTableRowData>({
  table,
  isSelectDropdown = true,
  itemLabel = 'items',
}: TableSelectionInfobarProps<TData>) => {
  const {
    getSelectedRowModel,
    getState,
    setSelectAllRecords,
    options: { rowCount, manualPagination, data },
  } = table
  const { pagination, isAllRowsSelected } = getState()
  const selectedRowsCount = getSelectedRowModel().rows.length
  const isSelectedRowsLessThanPageSize = selectedRowsCount < pagination?.pageSize
  const totalRecords = manualPagination ? rowCount : data?.length

  const toggleSelectAll = () => {
    if (isAllRowsSelected) {
      table.resetRowSelection() // Clear all rows
      setSelectAllRecords(false)
    } else {
      table.getToggleAllRowsSelectedHandler()
      setSelectAllRecords(true)
    }
  }

  if (isSelectedRowsLessThanPageSize) return null

  return (
    <div className="bg-muted p-2 sticky mx-6">
      <div className="flex items-center gap-4 justify-center">
        {isAllRowsSelected ? (
          <Typography variant="S/400" className="font-inter normal-case tracking-normal">
            All <span className="font-jetbrains text-xs-400 tabular-nums">{totalRecords}</span>{' '}
            {itemLabel} selected.{' '}
            <Button variant="ghost" className="underline" onClick={toggleSelectAll}>
              Clear Selection
            </Button>
          </Typography>
        ) : (
          <Typography variant="S/400" className="font-inter normal-case tracking-normal">
            All <span className="font-jetbrains text-xs-400 tabular-nums">{selectedRowsCount}</span>{' '}
            {itemLabel} selected.{' '}
            {isSelectDropdown && (
              <Button variant="ghost" className="underline" onClick={toggleSelectAll}>
                Select all {totalRecords} {itemLabel}
              </Button>
            )}
          </Typography>
        )}
      </div>
    </div>
  )
}
